import React, { ForwardRefRenderFunction } from 'react';
import { ISmartProps } from './types';
import { iconStyles, iconBeforeStyles, iconAfterStyles, StyledLink, wrapperStyle } from './styles';
import { MIcon } from '../Icon';
import { useStyletron } from '../../Themes';
import { ComponentWithAs } from '../../Utilities/types/As';
import { StyletronComponent } from 'styletron-react';
import { getBaseCustomProps } from '../../Utilities/types/SharedProps';
import { StyledLinkAsButton, StyledElementWithCustomStyle } from './view';
import * as meta from '../../meta-data.json';

export type LinkProps = Omit<ISmartProps, 'css'>;

const _Link: ComponentWithAs<any, LinkProps> = props => {
	const { iconAfter, iconBefore, tabIndex = 0, as = 'a', size = 'default', ...rest } = props;
	const [css, theme]: any = useStyletron();
	const Component: StyletronComponent<any> =
		as === 'a' ? StyledLink : as === 'button' ? StyledLinkAsButton : StyledElementWithCustomStyle({ size, theme });

	return (
		<Component
			data-epo={`epo-link-${meta.version}`}
			part={`epo-link-${meta.version}`}
			tabIndex={tabIndex}
			{...rest}
			ref={props['root-ref']}
			{...getBaseCustomProps(props)}
			size={size}
			className={`${css(wrapperStyle())} ${props.className}`}
		>
			{(props.iconBefore && typeof props.iconBefore == "string") && (
				<MIcon
					icon={props.iconBefore as string}
					size={size}
					className={css({ ...iconBeforeStyles(props, theme), ...iconStyles(props, theme) })}
					aria-hidden="true"
					focusable={false}
				/>
			)}
			{(props.iconBefore && typeof props.iconBefore !== "string") && props.iconBefore}
			{props.children}
			{/* {TODO: review v3.0 :: check disabled icons (before and after)} */}
			{(props.iconAfter && typeof props.iconAfter === "string") && (
				<MIcon
					icon={props.iconAfter}
					size={size}
					className={css({
						...iconAfterStyles(props, theme),
						...iconStyles({ ...props, disabled: props.disabled }, theme),
					})}
					aria-hidden="true"
					focusable={false}
				/>
			)}
			{(props.iconAfter && typeof props.iconAfter !== "string") && props.iconAfter}
		</Component>
	);
};

export const FunctionalRenderedLink: ForwardRefRenderFunction<typeof _Link, LinkProps & { as?: any }> = (
	props: LinkProps & { as?: any },
	ref,
) => (
	<_Link {...props} root-ref={ref}>
		{props.children}
	</_Link>
);

export const Link = React.forwardRef(FunctionalRenderedLink);
